import memoize from 'memoize-one';
import { useCallback, useMemo } from 'react';
import cloneDeep from 'lodash/cloneDeep';

import type { NavigationPolicy } from '@atlassian/embedded-confluence/navigationPolicy';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { transformNestedTablesIncomingDocument, transformNestedTableNodeOutgoingDocument } from '@atlaskit/adf-utils/nested-table-transform';

import {
	CONTEXT_PATH as CC_CONTEXT_PATH,
	isHashedOnlyURL,
} from '@confluence/route-manager-utils/entry-points/url.ts';

const isAdfContent = (content: any): content is ADFEntity => {
	return (
		content &&
		typeof content === 'object' &&
		content.type === 'doc' &&
		Array.isArray(content.content)
	);
};

/**
 * If a shim URL is provided by the embedding parent product, any URL that navigates to Confluence Pages
 * will be converted to the URL of the parent product. 3 functions are provided: shimUrl(), shimAdfUrls(),
 * shimHtmlUrls(). Both absolute URLs and relative URLs are supported. Since we are applying shimming logic, all shimmed URLs are absolute URLs with domain.
 *
 * shimUrl() is to shim any given Confluence Page URL into the URL of the embedding parent product
 * e.g. Given shimUrl is "/jsm",
 * Given Confluence Page URL "http://abc.atlassian.net/wiki/spaces/X/123" would be converted to "http://abc.atlassian.net/jsm/spaces/X/123".
 * Given Confluence Page URL "/wiki/spaces/X/123" would be converted to "http://abc.atlassian.net/jsm/spaces/X/123".
 *
 * shimAdfUrls() is to shim all Confluence Page URLs inside the given atlassian document format (ADF), which is a JSON object, into the URLs of the embedding parent product
 * e.g. Given shimUrl is "/jsm",
 * Given ADF { "href": "http://abc.atlassian.net/wiki/spaces/X/123" } would be converted to { "href": "http://abc.atlassian.net/jsm/spaces/X/123" }
 * Given ADF { "href": "/wiki/spaces/X/123" } would be converted to { "href": "http://abc.atlassian.net/jsm/spaces/X/123" }
 *
 * shimHtmlUrls() is to shim all Confluence Page URLs inside the given HTML string into the URLs of the embedding parent product
 * e.g. Given shimUrl is "/jsm",
 * Given HTML string <a href="http://abc.atlassian.net/wiki/spaces/X/123"> would be converted to <a href="http://abc.atlassian.net/jsm/spaces/X/123">.
 * Given HTML string <a href="/wiki/spaces/X/123"> would be converted to <a href="http://abc.atlassian.net/jsm/spaces/X/123">.
 */
export function useShimUrl(navigationPolicy: NavigationPolicy | undefined): {
	shimUrl: (urlToShim: string) => string;
	shimAdfUrls: (adf: object) => object;
	shimHtmlUrls: (html: string | null) => string | null;
} {
	const shimTemplate = navigationPolicy?.shimUrl;
	const domainNamesToReplace = navigationPolicy?.domainNamesToReplace;
	const spaceKeyFilter = navigationPolicy?.spaceKeyFilter;

	const shimUrl = useCallback(
		(urlToShim: string): string => {
			return shimThisUrl(urlToShim, shimTemplate, domainNamesToReplace, spaceKeyFilter);
		},
		[shimTemplate, domainNamesToReplace, spaceKeyFilter],
	);

	const shimAdfUrls = useCallback(
		(adf: object) => {
			if (!shimTemplate || !adf || !Object.keys(adf).length) {
				return adf;
			}
			const adfClone = cloneDeep(adf);
			replaceCcPageUrlsInAdf(adfClone, shimTemplate, domainNamesToReplace, spaceKeyFilter);

			return adfClone;
		},
		[shimTemplate, domainNamesToReplace, spaceKeyFilter],
	);

	const shimHtmlUrls = useCallback(
		(html: string | null) => {
			if (!shimTemplate || !html) {
				return html;
			}

			// shimHtmlUrls is used on legacy macros, but some legacy macros return stringified ADFEntity
			// Check if the input is a JSON-stringified ADFEntity
			try {
				const parsedContent = JSON.parse(html);
				if (isAdfContent(parsedContent)) {
					// It's a valid ADF document - check if it needs nested table transformation
					if (html.includes('"extensionKey":"nested-table"')) {
						const processedAdf = shimAdfWithNestedTables(parsedContent, shimAdfUrls);
						return JSON.stringify(processedAdf);
					} else {
						// ADF without nested tables - just delegate to shimAdfUrls and stringify the result
						const shimmedAdf = shimAdfUrls(parsedContent);
						return JSON.stringify(shimmedAdf);
					}
				}
			} catch {
				// Not valid JSON, fall through to HTML processing
			}

			// Process as HTML string (for non-ADF content)
			try {
				return shimDomUrls(
					new DOMParser().parseFromString(html, 'text/html'),
					shimTemplate,
					domainNamesToReplace,
					spaceKeyFilter,
				).body.innerHTML;
			} catch {
				return html;
			}
		},
		[shimTemplate, domainNamesToReplace, spaceKeyFilter, shimAdfUrls],
	);

	return useMemo(
		() => ({
			shimUrl,
			shimAdfUrls,
			shimHtmlUrls,
		}),
		[shimUrl, shimAdfUrls, shimHtmlUrls],
	);
}

function shimDomUrls(
	dom: Document,
	shimTemplate: string,
	domainNamesToReplace?: string[],
	spaceKeyFilter?: string[],
): Document {
	const anchors = dom.getElementsByTagName('a');
	Array.from(anchors).forEach((a) => {
		a.href = shimThisUrl(a.href, shimTemplate, domainNamesToReplace, spaceKeyFilter);
	});

	const imgs = dom.getElementsByTagName('img');
	Array.from(imgs).forEach((img) => {
		img.src = shimThisUrl(img.src, shimTemplate, domainNamesToReplace, spaceKeyFilter);
	});

	return dom;
}

function shimThisUrl(
	urlToShim: string,
	shimTemplate: string | undefined,
	domainNamesToReplace: string[] | undefined,
	spaceKeyFilter?: string[] | undefined,
): string {
	if (
		!shimTemplate ||
		!urlToShim ||
		isHashedOnlyURL(urlToShim) ||
		!isUrlForShimmedSpace(urlToShim, spaceKeyFilter)
	) {
		return urlToShim;
	}

	try {
		const baseUrl = document.baseURI || window.location.toString();
		const absUrlWithDomainToShim = getAbsUrlWithDomain(urlToShim, baseUrl);
		const urlPrefixToShim = ccAbsUrlWithDomain(baseUrl);
		const urlShimTemplate = shimAbsUrlWithDomain(shimTemplate, baseUrl);
		return getShimmedUrl(
			absUrlWithDomainToShim,
			urlPrefixToShim,
			urlShimTemplate,
			baseUrl,
			domainNamesToReplace,
		);
	} catch {
		return urlToShim;
	}
}

function isUrlForShimmedSpace(url: string, spaceKeyFilter?: string[]): boolean {
	// If no filter, shim all URLs (backwards compatibility)
	if (!spaceKeyFilter?.length) {
		return true;
	}

	// Regex to extract spaceKey from /spaces/<spaceKey>/
	const match = url.match(/\/spaces\/([^/]+)/);
	if (match) {
		// If URL contains /spaces/<spaceKey>, only shim if spaceKey is in the filter
		const spaceKey = match[1];
		return spaceKeyFilter.includes(spaceKey);
	}

	// For other /wiki URLs (e.g., share links), retain previous behavior and shim
	return true;
}

const ccAbsUrlWithDomain = memoize((base: string) =>
	appendSlash(getAbsUrlWithDomain(CC_CONTEXT_PATH, base)),
);
const shimAbsUrlWithDomain = memoize((urlShimTemplate: string, base: string) =>
	appendSlash(getAbsUrlWithDomain(urlShimTemplate, base)),
);

function getAbsUrlWithDomain(url: string, base: string): string {
	return new URL(url, base).toString();
}

function getShimmedUrl(
	absUrlWithDomainToShim: string,
	urlPrefixToShim: string,
	urlShimTemplate: string,
	baseUrl: string,
	domainNamesToReplace?: string[],
) {
	if (absUrlWithDomainToShim.includes(CC_CONTEXT_PATH)) {
		//We can only rebase or shim /wiki URLs
		const absUrlObj = new URL(absUrlWithDomainToShim);
		const baseUrlObj = new URL(baseUrl);
		//Check if the URL should be rebased
		if (domainNamesToReplace?.includes(absUrlObj.hostname)) {
			//Rebase the URL by replacing the origin string of absUrlWithDomainToShim with that of baseUrl
			const rebasedAbsUrlToShim = absUrlWithDomainToShim.replace(
				absUrlObj.origin,
				baseUrlObj.origin,
			);
			//Shimming the rebased URL
			return rebasedAbsUrlToShim.replace(urlPrefixToShim, urlShimTemplate);
		}
	}
	return absUrlWithDomainToShim.replace(urlPrefixToShim, urlShimTemplate);
}

function appendSlash(url: string): string {
	if (!url.endsWith('/')) {
		return `${url}/`;
	}
	return url;
}

const URL_ADF_KEYS = ['url', 'href'];
function replaceCcPageUrlsInAdf(
	adfJson: { [key: string]: any },
	shimTemplate: string,
	domainNamesToReplace?: string[],
	spaceKeyFilter?: string[],
): void {
	Object.entries(adfJson).forEach(([key, value]) => {
		if (!value) {
			return;
		}
		if (URL_ADF_KEYS.includes(key) && typeof value === 'string') {
			adfJson[key] = shimThisUrl(value, shimTemplate, domainNamesToReplace, spaceKeyFilter);
		} else if (typeof value === 'object') {
			replaceCcPageUrlsInAdf(value, shimTemplate, domainNamesToReplace, spaceKeyFilter);
		}
	});
}

/**
 * Shims URLs in ADF content that contains nested tables.
 * This function processes ADF document content with nested tables by:
 * 1. Transforming nested tables (incoming)
 * 2. Applying URL shimming via shimAdfUrls
 * 3. Transforming nested tables (outgoing)
 *
 * @param adfContent - Parsed ADF document content (already validated)
 * @param shimAdfUrls - Function to apply URL shimming to ADF objects
 * @returns Processed ADF object with shimmed URLs and transformed nested tables
 */
function shimAdfWithNestedTables(
	adfContent: ADFEntity,
	shimAdfUrls: (adf: object) => object,
): object {
	// Process ADF document with nested tables - transform and delegate to shimAdfUrls
	const { transformedAdf } = transformNestedTablesIncomingDocument(adfContent);
	const shimmedAdf = shimAdfUrls(transformedAdf);
	return transformNestedTableNodeOutgoingDocument(shimmedAdf as ADFEntity);
}
